import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/cn';

/**
 * The variants, declared once.
 *
 * <p>Written with `cva` rather than as a chain of conditionals so a caller picks
 * a named variant instead of assembling classes. The point is not brevity: it is
 * that "what a primary button looks like" has one definition, so changing it
 * changes every one.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-strong)] ' +
          'focus-visible:ring-[var(--color-accent)]',
        secondary:
          'border border-[var(--color-rule)] bg-white text-[var(--color-ink)] hover:bg-gray-50 ' +
          'focus-visible:ring-[var(--color-ink-faint)]',
        ghost:
          'text-[var(--color-ink-soft)] hover:bg-gray-100 focus-visible:ring-[var(--color-ink-faint)]',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-5 text-[15px]',
        lg: 'h-12 px-6 text-base',
      },
      full: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

type Props = ComponentProps<'button'> &
  VariantProps<typeof button> & {
    /** Render as the child element instead of a `button` — a link that looks like one. */
    asChild?: boolean;
  };

export function Button({ className, variant, size, full, asChild, ...props }: Props) {
  const Component = asChild ? Slot : 'button';
  return <Component className={cn(button({ variant, size, full }), className)} {...props} />;
}
